import { useState } from "react";
import { Task, TaskStatus } from "@/types/task-management";
import TaskListItem from "./TaskListItem";
import TaskDialog from "./TaskDialog";
import CreateTaskDialog from "./CreateTaskDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  ChevronDown, 
  Filter, 
  Plus, 
  Search, 
  SlidersHorizontal,
  Sun,
  Loader2,
  EyeOff,
  CheckCircle
} from "lucide-react";

interface TaskListViewProps {
  tasks: Task[];
  onTaskCreate: (task: Partial<Task>) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onTaskStatusChange: (taskId: string, status: TaskStatus) => void;
  onAddToMyDay?: (taskId: string, added: boolean) => void;
  onDeleteTask?: (taskId: string) => void;
  onPostponeTask?: (taskId: string) => void;
  isLoading?: boolean;
}

export default function TaskListView({ 
  tasks, 
  onTaskCreate, 
  onTaskUpdate, 
  onTaskStatusChange,
  onAddToMyDay,
  onDeleteTask,
  onPostponeTask,
  isLoading = false
}: TaskListViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("due_date");
  const [showMyDayOnly, setShowMyDayOnly] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  
  // Filtrar tarefas
  const filteredTasks = tasks.filter(task => {
    // Filtro de texto
    const matchesSearch = 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Filtro de status
    const matchesStatus = 
      statusFilter === "all" || 
      task.status === statusFilter;
    
    // Filtro "Meu dia"
    const matchesMyDay = 
      !showMyDayOnly || 
      task.added_to_my_day;
    
    // Filtro para ocultar tarefas concluídas
    const matchesCompletedFilter = 
      !hideCompleted || 
      task.status !== "completed";
    
    return matchesSearch && matchesStatus && matchesMyDay && matchesCompletedFilter;
  });
  
  // Ordenar tarefas
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // Primeiro, priorizar tarefas "a fazer" (todo)
    if (a.status === "todo" && b.status !== "todo") return -1;
    if (b.status === "todo" && a.status !== "todo") return 1;
    
    // Se ambas as tarefas têm o mesmo status ou nenhuma é "todo", aplicar o critério de ordenação selecionado
    if (sortBy === "due_date") {
      // Se não há data de vencimento, colocar por último
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    }
    
    if (sortBy === "priority") {
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      return priorityOrder[a.priority as keyof typeof priorityOrder] - 
             priorityOrder[b.priority as keyof typeof priorityOrder];
    }
    
    if (sortBy === "title") {
      return a.title.localeCompare(b.title);
    }
    
    // Por padrão, ordenar por data de criação (mais recente primeiro)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
  
  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
  };
  
  // Manipulador para encaminhar a ação "Adicionar ao meu dia"
  const handleAddToMyDay = (taskId: string, added: boolean) => {
    if (onAddToMyDay) {
      onAddToMyDay(taskId, added);
    }
  };
  
  // Manipulador para excluir tarefa
  const handleDeleteTask = (taskId: string) => {
    if (onDeleteTask) {
      onDeleteTask(taskId);
    }
  };
  
  // Contar tarefas concluídas para exibir no resumo
  const completedTasksCount = tasks.filter(task => task.status === "completed").length;
  
  return (
    <div className="space-y-4">
      {/* Barra de ferramentas simplificada */}
      <div className="flex flex-wrap items-center gap-2 pb-2">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Procurar tarefas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="todo">A fazer</SelectItem>
            <SelectItem value="in_progress">Em progresso</SelectItem>
            <SelectItem value="waiting">Aguardando</SelectItem>
            <SelectItem value="completed">Concluído</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="due_date">Data de vencimento</SelectItem>
            <SelectItem value="priority">Prioridade</SelectItem>
            <SelectItem value="title">Título</SelectItem>
            <SelectItem value="created_at">Data de criação</SelectItem>
          </SelectContent>
        </Select>
        
        <Button 
          variant={showMyDayOnly ? "default" : "outline"} 
          size="sm"
          onClick={() => setShowMyDayOnly(!showMyDayOnly)}
          className="gap-1"
        >
          <Sun className={`h-4 w-4 ${showMyDayOnly ? "text-white" : "text-amber-500"}`} />
          <span className="hidden sm:inline">Meu dia</span>
        </Button>
        
        <Button 
          variant={hideCompleted ? "default" : "outline"}
          size="sm"
          onClick={() => setHideCompleted(!hideCompleted)}
          className="gap-1"
        >
          <EyeOff className={`h-4 w-4 ${hideCompleted ? "text-white" : "text-muted-foreground"}`} />
          <span className="hidden sm:inline">Concluídas</span>
        </Button>
      </div>
      
      {/* Contador de tarefas concluídas quando estão ocultas */}
      {hideCompleted && completedTasksCount > 0 && (
        <div 
          className="flex items-center justify-between p-2 bg-muted rounded-md cursor-pointer"
          onClick={() => setHideCompleted(false)}
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>{completedTasksCount} {completedTasksCount === 1 ? 'tarefa concluída está oculta' : 'tarefas concluídas estão ocultas'}</span>
          </div>
          <Button variant="ghost" size="sm">Mostrar</Button>
        </div>
      )}
      
      {/* Lista de tarefas */}
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-primary/70" />
        </div>
      ) : (
        <div className="space-y-1">
          {sortedTasks.length > 0 ? (
            sortedTasks.map(task => (
              <TaskListItem
                key={task.id}
                task={task}
                onStatusChange={(status) => onTaskStatusChange(task.id, status as TaskStatus)}
                onClick={() => handleTaskClick(task.id)}
                onAddToMyDay={(added) => onAddToMyDay ? onAddToMyDay(task.id, added) : undefined}
                onDelete={() => onDeleteTask ? onDeleteTask(task.id) : undefined}
                onPostpone={() => onPostponeTask ? onPostponeTask(task.id) : undefined}
              />
            ))
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              {searchQuery || statusFilter !== "all" || showMyDayOnly || hideCompleted ? (
                <p>Nenhuma tarefa corresponde aos filtros aplicados.</p>
              ) : (
                <p>Nenhuma tarefa encontrada. Crie uma nova tarefa para começar.</p>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Dialog para editar tarefa */}
      {selectedTaskId && (
        <TaskDialog
          task={tasks.find(t => t.id === selectedTaskId)}
          open={!!selectedTaskId}
          onOpenChange={(open) => {
            if (!open) setSelectedTaskId(null);
          }}
          onSubmit={(updates) => {
            onTaskUpdate(selectedTaskId, updates);
            setSelectedTaskId(null);
          }}
          onDelete={() => {
            if (onDeleteTask) {
              onDeleteTask(selectedTaskId);
              setSelectedTaskId(null);
            }
          }}
        />
      )}
    </div>
  );
} 